import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useEngagementReport } from "@/hooks/use-system-reporting"
import { getPageHeader } from "@/constants"
import { PageContainer } from "@/components/ui/page-container"
import { PageHeader } from "@/components/ui/page-header"
import { PageHeaderLeading } from "@/components/navigation/page-header-leading"
import { PageHeaderStack } from "@/components/navigation/page-header-stack"
import {
  PageHeaderActionSecondary,
  PageHeaderActions,
} from "@/components/navigation/page-header-actions"
import { RefreshCw, BarChart3 } from "lucide-react"
import { dashboardMetricGridClassName } from "@/components/dashboard/dashboard-metric-grid-classes"

interface AdminReportingPageProps {
  headerPath?: string
  title?: string
  description?: string
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string
  value: number | string | null | undefined
  hint?: string
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl tabular-nums">
          {value === null || value === undefined ? "—" : value.toLocaleString?.() ?? value}
        </CardTitle>
      </CardHeader>
      {hint ? (
        <CardContent className="pt-0 text-xs text-muted-foreground">{hint}</CardContent>
      ) : null}
    </Card>
  )
}

export function AdminReportingPage({
  headerPath = '/platform/engagement',
  title = 'Engagement',
  description = 'How your users are engaging across the platform',
}: AdminReportingPageProps) {
  const [dateRange, setDateRange] = useState("30d")
  const queryClient = useQueryClient()
  const { data: report, isLoading, error, isFetching } = useEngagementReport({ date_range: dateRange })

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['engagement-report'] })
  }

  const core = report?.core
  const domain = report?.domain
  const rangeHint = `in the selected period`

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          {...getPageHeader(headerPath, title, description)}
          icon={<BarChart3 className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              secondary={
                <PageHeaderActionSecondary onClick={handleRefresh} disabled={isFetching}>
                  <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
                  Refresh
                </PageHeaderActionSecondary>
              }
            />
          }
        />

        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="h-8 w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="365d">Last year</SelectItem>
          </SelectContent>
        </Select>
      </PageHeaderStack>

      {error ? (
        <Card>
          <CardContent className="py-6 text-sm text-destructive">
            Failed to load engagement report. Try refreshing.
          </CardContent>
        </Card>
      ) : isLoading || !core ? (
        <div className={dashboardMetricGridClassName(4)}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : (
        <>
          <div className={dashboardMetricGridClassName(4)}>
            <MetricCard label="Total users" value={core.total_users} />
            <MetricCard label="Active users" value={core.active_users} />
            <MetricCard label="Signups" value={core.signups} hint={rangeHint} />
            <MetricCard label="Recent logins" value={core.recent_logins} hint={rangeHint} />
            <MetricCard label="Conversations" value={core.threads} hint="all time" />
            <MetricCard label="New conversations" value={core.threads_in_range} hint={rangeHint} />
            {typeof core.feedback_items === 'number' && (
              <MetricCard label="Feedback items" value={core.feedback_items} hint={rangeHint} />
            )}
            {domain?.kind === 'employment' && (
              <>
                <MetricCard label="Opportunities" value={domain.opportunities} hint={rangeHint} />
                <MetricCard label="Applications" value={domain.applications} hint={rangeHint} />
              </>
            )}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Users by role</CardTitle>
              <CardDescription>Front-facing role distribution</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(core.group_distribution).length === 0 ? (
                <p className="text-sm text-muted-foreground">No role data available.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(core.group_distribution).map(([group, count]) => (
                    <div key={group} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{group.replace(/_/g, ' ')}</span>
                      <span className="tabular-nums text-muted-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </PageContainer>
  )
}
